import { createContext, useContext } from "react";
import type { WidgetChangeOptions } from "./types.js";

/**
 * What `useWidget(widgetId)` returns to the canvas-side adapter for SQL-
 * storage widgets. Mirrors a TanStack Query result shape but is library-
 * agnostic so a different host (Storybook, tests) can implement it without
 * pulling TanStack Query.
 */
export interface WidgetEntityHandle<TParams = unknown> {
  readonly data: TParams | undefined;
  /** Persisted params schema version returned by the owning backend. */
  readonly schemaVersion?: number;
  readonly isLoading: boolean;
  readonly error: Error | null;
  /**
   * Imperative patch — typically debounced by the implementation.
   *
   * PB-4923 note: `next` is still the COMPLETE params, and that is deliberate.
   * Whether the host sends a bounded set of field/item operations or a
   * whole-params write is the host's decision at send time, made against the
   * last server-acknowledged value; this package stays data-blind and does not
   * know the transport.
   *
   * Assign-person d25: with `options.grantViewerIfMissing` (a person picked in
   * a person field) the host sends that save at once, may add the person as
   * Viewer in it, and returns a promise that settles with it. A rejection's
   * `message` is the text to show under the field.
   */
  readonly patch: (
    next: TParams,
    options?: WidgetChangeOptions,
  ) => Promise<void> | void;
  /**
   * PB-4923 AC5 — declare that this person is EDITING one field, right now.
   *
   * Call it when a control takes focus; call the returned teardown on blur and
   * on unmount. The host turns that into a server-observable editing session so
   * an approved Player AI change skips the field and says who is editing it.
   *
   * It has to be focus-driven, not change-driven. Somebody who has clicked into
   * a field and is composing has changed nothing yet, and that is precisely the
   * window in which an AI apply would overwrite a draft that only exists on
   * their screen.
   *
   * Holding one grants NOTHING: it is liveness, not a lock and not a
   * permission. The field's own revision is what protects a committed edit, so
   * a lease that never opens degrades a message, never the protection.
   *
   * `targetKey` is the canonical address the merge register uses
   * (`branches[br-1]/causes[c-2]/text`) — build it with `widgetTargetKey` from
   * `@playbookai/widget-schemas` rather than by string concatenation.
   *
   * Optional so a host that has not wired it (Storybook, a test) simply does
   * not lease, matching the `useWidgetActions()?.x` convention.
   */
  readonly beginFieldEdit?: (targetKey: string) => () => void;
}

/**
 * Host-provided resolver for SQL-backed widget entities. Hosts that render
 * SQL-storage widgets on a canvas must wrap the canvas in a
 * `<WidgetEntityResolverProvider value={...}>` that knows how to fetch +
 * patch widgets by id (typically via TanStack Query against
 * `/v1/widgets/:id`).
 *
 * This keeps `@playbookai/ui` data-blind — the package depends on the
 * shape of the resolver but not on any specific fetching library.
 */
export interface WidgetEntityResolver {
  readonly useWidget: <TParams>(
    widgetId: string,
  ) => WidgetEntityHandle<TParams>;
}

const Ctx = createContext<WidgetEntityResolver | null>(null);

export const WidgetEntityResolverProvider = Ctx.Provider;

export function useWidgetEntityResolver(): WidgetEntityResolver | null {
  return useContext(Ctx);
}
